<template>
  <div class="position-relative">
    <div class="d-flex align-center justify-end" style="margin-top: -30px;">
      <help-dialog-workflow-read-models />
    </div>
    <div class="mt-4">
      <div v-for="group in groupedReadModels" :key="group.entityId || '__unlinked__'" class="mb-8">
        <div class="d-flex align-center mb-3">
          <v-icon class="mr-2" size="small">mdi-database</v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ group.entityName }}</span>
          <v-chip density="compact" class="ml-2" size="small">{{ group.readModels.length }}</v-chip>
        </div>
        <div class="d-flex flex-wrap ga-4 align-start">
          <read-model-card
            v-for="readModel in group.readModels"
            :key="readModel.id"
            :schema="readModel"
            :usageStatus="readModelUsageStatus.get(readModel.id)"
            :deleting="deletingId === readModel.id"
            @deleteSchema="handleDeleteReadModel"
            @editSchema="openEditDialog"
          />
          <div
            class="add-card-placeholder d-flex align-center justify-center"
            :style="{ borderColor: cardColor }"
            :class="{ 'add-card-placeholder--disabled': store.state.isWorkflowDisabled || addingEntityId === group.entityId }"
            @click="!store.state.isWorkflowDisabled && addReadModel(group.entityId)"
          >
            <v-progress-circular v-if="addingEntityId === group.entityId" :size="24" :width="2" indeterminate :color="cardColor" />
            <v-icon v-else :color="cardColor" size="28">mdi-plus</v-icon>
          </div>
        </div>
      </div>
    </div>

    <edit-query-dialog v-model="editDialogOpen" :schema="editingSchema" />
    <delete-confirmation-dialog
      v-model="openDeleteDialog"
      :title="dialogInfo.title"
      :text="dialogInfo.text"
      :onDelete="dialogInfo.action"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import ReadModelCard from '@/components/ReadModelCard.vue'
import EditQueryDialog from '@/components/EditQueryDialog.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import HelpDialogWorkflowReadModels from '@/components/HelpDialogWorkflowReadModels.vue'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'
import workflowApi from '@/api/workflowApi'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'

const store = useStore()
const { cardTypeColor } = useDomainModel()
const { deleteReadModel } = useReadModelActions()

const addingEntityId = ref(undefined)
const deletingId = ref(null)
const openDeleteDialog = ref(false)
const dialogInfo = ref({ title: '', text: '', action: null })
const editDialogOpen = ref(false)
const editingSchemaId = ref(null)

const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const cardColor = computed(() => cardTypeColor(queryCardType.value) || '#eeeeee')
const readModels = computed(() => (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.QUERY))
const entities = computed(() => (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.ENTITY))

const editingSchema = computed(() => (
  editingSchemaId.value
    ? readModels.value.find((rm) => rm.id === editingSchemaId.value) ?? null
    : null
))

const readModelUsageStatus = computed(() => {
  const usedSchemaIds = new Set()
  const domainEvents = workflow.value?.domainEvents ?? []

  domainEvents.forEach((event) => {
    event.cards?.forEach((card) => {
      if (!card.schemaId) {
        return
      }
      if (card.cardType?.domainModelRole === DomainModelRole.READ_MODEL) {
        usedSchemaIds.add(card.schemaId)
      }
    })
  })

  const statusMap = new Map()
  readModels.value.forEach((rm) => {
    if (!usedSchemaIds.has(rm.id)) {
      statusMap.set(rm.id, 'unused')
    }
  })
  return statusMap
})

const groupedReadModels = computed(() => {
  const entityMap = new Map()
  const unlinked = []

  readModels.value.forEach((rm) => {
    if (rm.entityId) {
      if (!entityMap.has(rm.entityId)) {
        entityMap.set(rm.entityId, [])
      }
      entityMap.get(rm.entityId).push(rm)
    } else {
      unlinked.push(rm)
    }
  })

  const groups = []

  entityMap.forEach((rms, entityId) => {
    const entity = entities.value.find((e) => e.id === entityId)
    groups.push({
      entityId,
      entityName: entity?.name || 'Unknown Entity',
      readModels: rms
    })
  })

  groups.sort((a, b) => a.entityName.localeCompare(b.entityName))

  groups.push({
    entityId: null,
    entityName: 'Not linked to entity',
    readModels: unlinked
  })

  return groups
})

const openEditDialog = (schemaId) => {
  editingSchemaId.value = schemaId
  editDialogOpen.value = true
}

const addReadModel = async (entityId) => {
  addingEntityId.value = entityId
  try {
    const existingNames = new Set(readModels.value.map((s) => s.name.toLowerCase()))

    const counter = Array.from({ length: readModels.value.length + 1 }, (_, i) => i + 1)
      .find((n) => !existingNames.has(`new read model ${n}`))

    const candidateName = `New Read Model ${counter}`

    const newSchema = {
      name: candidateName,
      type: SchemaType.QUERY,
      fields: []
    }

    if (entityId) {
      newSchema.entityId = entityId
    }

    const newSchemas = [...(workflow.value.schemas || []), newSchema]

    await workflowApi.updateSchemas({
      schemas: newSchemas.length > 0 ? newSchemas : undefined,
      workflow: workflow.value
    })
  } finally {
    addingEntityId.value = undefined
  }
}

const getReadModelUsageCount = (schemaId) => {
  const domainEvents = workflow.value?.domainEvents ?? []
  let count = 0
  domainEvents.forEach((event) => {
    event.cards?.forEach((card) => {
      if (card.schemaId === schemaId) {
        count++
      }
    })
  })
  return count
}

const handleDeleteReadModel = async (schemaId) => {
  const usageCount = getReadModelUsageCount(schemaId)

  if (usageCount === 0) {
    deletingId.value = schemaId
    try {
      await deleteReadModel(schemaId)
    } finally {
      deletingId.value = null
    }

    return
  }

  openDeleteDialog.value = true
  dialogInfo.value = {
    title: 'Confirm read model deletion',
    text: `This read model is currently used by ${usageCount} card${usageCount > 1 ? 's' : ''} in the domain model. Deleting it will unlink those cards. Are you sure?`,
    action: () => deleteReadModel(schemaId)
  }
}
</script>

<style scoped>
.add-card-placeholder {
  min-width: 200px;
  max-width: 300px;
  height: 116px;
  border: 1px dashed;
  border-radius: 8px;
  background: var(--light-1);
  cursor: pointer;
  transition: opacity 0.2s;
  opacity: 0.5;
}

.add-card-placeholder:hover {
  opacity: 1;
}

.add-card-placeholder--disabled {
  cursor: default;
  opacity: 0.3;
  pointer-events: none;
}
</style>
